import { useCallback, useEffect, useState } from "react";

const MIN_CHECKS = 10;

const checkIfElementIsScrollingVertically = (element: Element) => {
  return element.scrollHeight > element.clientHeight;
};

const performChecks = (element: Element) => {
  const isCropped = checkIfElementIsScrollingVertically(element);
  const height = element.scrollHeight;

  return { isCropped, height };
};

export const useExpandableContentHeight = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [isCropped, setIsCropped] = useState(true);
  const [height, setHeight] = useState<number | null>(null);

  const performChecksAndUpdateState = useCallback((element: Element) => {
    const { isCropped, height } = performChecks(element);

    setHeight(height);
    setIsCropped(isCropped);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      performChecksAndUpdateState(entries[0].target);
    });

    const mutationObserver = new MutationObserver(() => {
      if (ref.current) {
        performChecksAndUpdateState(ref.current);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
      mutationObserver.observe(ref.current, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref, performChecksAndUpdateState]);

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() => {
        if (!ref.current) {
          return;
        }

        performChecksAndUpdateState(ref.current);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [ref, performChecksAndUpdateState]);

  // Routinely check for updates to the height, in case images with auto-height are used
  // Stop after 1s if the height becomes stable
  useEffect(() => {
    let checksCount = 0;
    let lastHeight: number | null = null;

    const interval = setInterval(() => {
      if (!ref.current) {
        return;
      }

      const { isCropped, height } = performChecks(ref.current);
      checksCount++;

      if (height === lastHeight && checksCount >= MIN_CHECKS) {
        clearInterval(interval);
        return;
      }

      lastHeight = height;
      setHeight(height);
      setIsCropped(isCropped);
    }, 100);

    return () => clearInterval(interval);
  }, [ref]);

  // eslint-disable-next-line sonarjs/no-identical-functions
  const refresh = useCallback(() => {
    if (!ref.current) {
      return;
    }

    performChecksAndUpdateState(ref.current);
  }, [ref, performChecksAndUpdateState]);

  return {
    isCropped,
    height,
    refresh,
  };
};
